"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import gsap from "gsap";

interface ScrollPromptProps {
  onHide?: () => void;
}

export default function ScrollPrompt({ onHide }: ScrollPromptProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);

  // Only render on client side
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted || !containerRef.current) return;

    const container = containerRef.current;
    const dot = container.querySelector(".scroll-dot");

    // Fade in the container
    gsap.fromTo(
      container,
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.6, ease: "power2.out" }
    );

    // Dot scroll animation - independent GSAP context
    if (dot) {
      const animation = gsap
        .timeline({ repeat: -1 })
        .fromTo(
          dot,
          {
            y: 3,
            opacity: 0,
          },
          {
            y: 3,
            opacity: 1,
            duration: 0.5,
            ease: "sine.inOut",
          }
        )
        .to(dot, {
          y: 7,
          opacity: 1,
          duration: 0.8,
          ease: "none",
        })
        .to(dot, {
          y: 7,
          opacity: 0,
          duration: 0.3,
          ease: "sine.out",
        });

      // Hide on scroll
      const handleScroll = () => {
        if (window.scrollY > 100) {
          gsap.to(container, {
            opacity: 0,
            duration: 0.6,
            onComplete: () => {
              onHide?.();
            },
          });
        }
      };

      const scrollTimer = setTimeout(() => {
        window.addEventListener("scroll", handleScroll);
      }, 1000);

      return () => {
        animation.kill();
        window.removeEventListener("scroll", handleScroll);
        clearTimeout(scrollTimer);
      };
    }
  }, [mounted, onHide]);

  const content = (
    <div
      ref={containerRef}
      className="scroll-prompt"
      style={{
        position: "fixed",
        top: "90vh",
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 9999,
      }}
    >
      <div className="scroll-prompt-container" style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <svg
          width="30"
          height="45"
          viewBox="0 0 24 36"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            x="2"
            y="2"
            width="20"
            height="32"
            rx="10"
            stroke="white"
            strokeWidth="1.5"
            fill="none"
          />
          <circle
            className="scroll-dot"
            cx="12"
            cy="10"
            r="1.5"
            fill="white"
          />
        </svg>
        <span
          style={{
            display: "block",
            color: "white",
            fontSize: "10px",
            letterSpacing: "0.15em",
            marginTop: "8px",
            textAlign: "center",
          }}
        >
          SCROLL
        </span>
      </div>
    </div>
  );

  // Only render portal on client side after mount
  if (!mounted) return null;

  return createPortal(content, document.body);
}
